import { Account } from '@/domain/usecases'
import { authorizationService } from '@/data/services/authorization/authorization-service'
import {
  normalizeFilterOptions,
  normalizeStatusOptions
} from '@/data/services/authorization/authorization-service-utils'

/**
 * Loads authorization data for company manager authorizations page
 * Pre-fetches status groups and filter options (including labels)
 * Includes defensive error handling to prevent page crashes
 * Normalizes all filter options to consistent {id, name} format
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export async function loadCompanyManagerAuthorizations(_account: Account.Model) {
  try {
    const [statusGroups, rawFilterOptions, sharedBudget, customFieldOptions, labelOptions] =
      await Promise.all([
        authorizationService.fetchStatusGroups().catch(err => {
          console.error('❌ Failed to fetch status groups:', err)
          return []
        }),
        authorizationService.fetchFilterOptions().catch(err => {
          console.error('❌ Failed to fetch filter options:', err)
          return {
            companies: [],
            departments: [],
            jobs: [],
            status: [],
            partners: [],
            labels: [],
            accountTypes: []
          }
        }),
        authorizationService.fetchCompanySharedBudget().catch(err => {
          console.error('❌ Failed to fetch shared budget:', err)
          return 0
        }),
        // Cargos/Departamentos vêm dos campos customizados (/management/options),
        // não do /user/statusOptions genérico.
        authorizationService.fetchCustomFieldOptions().catch(err => {
          console.error('❌ Failed to fetch custom field (jobs/departments) options:', err)
          return { departments: [], jobs: [] }
        }),
        // Etiquetas: o /user/statusOptions não retorna labels — buscamos de /authorization_label.
        authorizationService.fetchLabels().catch(err => {
          console.error('❌ Failed to fetch labels:', err)
          return []
        })
      ])

    // Normaliza todas as opções de filtro para o formato padronizado {id, name}
    const filterOptions = {
      companies: normalizeFilterOptions(rawFilterOptions.companies || []),
      // Cargos/Departamentos: campos customizados ({value,label} → {id,name}).
      departments: (customFieldOptions?.departments || []).map(d => ({
        id: String(d.value),
        name: d.label
      })),
      jobs: (customFieldOptions?.jobs || []).map(j => ({ id: String(j.value), name: j.label })),
      status: normalizeStatusOptions(
        (rawFilterOptions.status || []) as unknown as Array<{ [key: string]: unknown }>
      ),
      partners: normalizeFilterOptions(rawFilterOptions.partners || []),
      // Etiquetas vêm de /authorization_label (o statusOptions não as retorna).
      labels: normalizeFilterOptions(
        (labelOptions || []) as unknown as Array<{ [key: string]: unknown }>
      ),
      accountTypes: rawFilterOptions.accountTypes || []
    }

    return {
      statusGroups,
      filterOptions,
      sharedBudget
    }
  } catch (error) {
    console.error('Critical error loading authorization data:', error)
    // Return safe defaults to prevent complete page failure
    return {
      statusGroups: [],
      filterOptions: {
        companies: [],
        departments: [],
        jobs: [],
        status: [],
        partners: [],
        labels: [],
        accountTypes: []
      },
      sharedBudget: 0
    }
  }
}
